import React, { FC, useState, useCallback, SyntheticEvent } from 'react';
import { AutocompleteInputChangeReason, AutocompleteProps } from '@mui/material';
import { PopperPlacementType } from '@mui/material/Popper';
import { useTheme } from '@mui/material/styles';
import { TextFieldProps } from '@mui/material/TextField';
import debounce from 'lodash/debounce';
import moment from 'moment';

import { IJobValueDTO } from 'src/api/dtos';
import CommonJobRepository from 'src/api/repositories/common/v1/job.repository';
import { SearchBar, SearchIcon } from 'src/shared/components';
import { API_DATE_FORMAT } from 'src/shared/constants';
import { useInfiniteScroll } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { handleScrollEnd } from 'src/shared/utils';

const SCROLL_OFFSET = 100;

enum InputStateEnums {
  SEARCHING = 'searching',
  SELECTING = 'selecting',
}

interface Props {
  level: number;
  date?: string;
  jobValue?: IJobValueDTO;
  onSelect?: (level: number, name: string, jobValue: IJobValueDTO) => void;
  disabled?: boolean;
  placeholder?: string;
  placement?: PopperPlacementType;
  showTooltip?: boolean;
  error?: boolean;
  isWidthMaxContent?: boolean;
  showSearchIcon?: boolean;
  sx?: AutocompleteProps<any, any, any, any>['sx'];
  _input?: TextFieldProps['sx'];
}

export const JobSelector: FC<Props> = ({
  date,
  level,
  jobValue,
  onSelect,
  disabled,
  placeholder,
  placement,
  showTooltip,
  error,
  showSearchIcon = true,
  isWidthMaxContent,
  sx,
  _input,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [inputState, setInputState] = useState<InputStateEnums>(InputStateEnums.SELECTING);

  const {
    collection: jobs,
    paginatedData: paginatedJobs,
    handleLoadMore,
    isLoadingMore,
    isRefreshing,
  } = useInfiniteScroll<IJobValueDTO>({
    useQuery: CommonJobRepository.endpoints.useGetJobsQuery,
    apiParams: {
      jobLevel: level,
      startDate: date || moment().format(API_DATE_FORMAT),
      endDate: date,
      searchTerm: inputState === InputStateEnums.SEARCHING ? searchTerm : '',
      perPage: 10,
    },
    skip: !open,
  });

  const loading = isRefreshing || isLoadingMore;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (jobValue: IJobValueDTO) => {
    if (!paginatedJobs || !paginatedJobs.name) {
      return;
    }

    onSelect?.(level, paginatedJobs.name, jobValue);
  };

  const handleOnChange = (_e: SyntheticEvent, value: IJobValueDTO | IJobValueDTO[] | null) => {
    if (!value || Array.isArray(value)) {
      return;
    }

    handleSelect(value);
  };

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (_: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') {
      setInputState(InputStateEnums.SELECTING);
    }

    if (reason === 'input') {
      setInputState(InputStateEnums.SEARCHING);
    }

    debounceSearchTermChange(value);
    setInputValue(value);
  };

  return (
    <SearchBar
      // Open
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      // Selected
      value={jobValue ?? null}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={handleOnChange}
      // Options
      options={jobs}
      getOptionLabel={(jobValue) => `${jobValue.value} - ${jobValue.description}`}
      filterOptions={(options) => options} // Don't filter options with inputValue
      // Loading
      loading={loading}
      // Input
      inputValue={inputValue}
      onInputChange={handleInputValueChange}
      // Component props
      InputProps={{
        startAdornment: <>{showSearchIcon && <SearchIcon color={theme.palette.action.active} />}</>,
      }}
      ListboxProps={{
        onScroll: (event) => handleScrollEnd(event, handleLoadMore, { loading: loading, scrollOffset: SCROLL_OFFSET }),
        role: 'list-box',
      }}
      componentsProps={{
        popper: {
          placement: placement,
          sx: { width: isWidthMaxContent ? 'max-content !important' : undefined },
        },
      }}
      // Settings
      placeholder={placeholder ?? t('employees.searchJobs')}
      inputTooltip={showTooltip && jobValue && `${jobValue.value} - ${jobValue.description}`}
      disabled={disabled}
      error={error}
      sx={sx}
      _input={_input}
      disableClearable
    />
  );
};
